import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHead, Field, Empty, inputCls, selectCls, btnPrimary, btnGhost, fmtDate } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { Contact, Plus, Pencil, Loader2, KeyRound, Lock, Unlock, QrCode, Trash2 } from "lucide-react";

const ROLE = { mecanico: "Mecânico", consultor: "Consultor", outro: "Outro" };
const EMPTY = { name: "", role: "mecanico", job_title: "", phone: "", salary: "", admission_date: "", birth_date: "", active: true, salary_payable: false, comm_parts_pct: "", comm_labor_pct: "", monthly_goal: "", comm_hour_rate: "" };

function EmployeeDialog({ open, onOpenChange, emp, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(emp ? { ...EMPTY, ...emp, salary: emp.salary ?? "", comm_parts_pct: emp.comm_parts_pct ?? "", comm_labor_pct: emp.comm_labor_pct ?? "", monthly_goal: emp.monthly_goal ?? "", comm_hour_rate: emp.comm_hour_rate ?? "" } : EMPTY); }, [open, emp]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { name: f.name, role: f.role, job_title: f.job_title || null, phone: f.phone || null, salary: f.salary === "" ? null : Number(f.salary),
      admission_date: f.admission_date || null, birth_date: f.birth_date || null, active: f.active, salary_payable: f.salary_payable,
      comm_parts_pct: f.comm_parts_pct === "" ? null : Number(f.comm_parts_pct), comm_labor_pct: f.comm_labor_pct === "" ? null : Number(f.comm_labor_pct),
      monthly_goal: f.monthly_goal === "" ? null : Number(f.monthly_goal), comm_hour_rate: f.comm_hour_rate === "" ? null : Number(f.comm_hour_rate) };
    try { emp ? await api.patch(`/employees/${emp.id}`, body) : await api.post("/employees", body); toast.success("Funcionário salvo"); onSaved(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="employee-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{emp ? "Editar funcionário" : "Novo funcionário"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Nome *" className="col-span-2"><Input data-testid="employee-name" value={f.name} onChange={set("name")} className={inputCls} /></Field>
          <Field label="Função no sistema"><select data-testid="employee-role" value={f.role} onChange={set("role")} className={selectCls}>{Object.entries(ROLE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
          <Field label="Cargo"><Input data-testid="employee-title" value={f.job_title || ""} onChange={set("job_title")} placeholder="Ex.: Eletricista" className={inputCls} /></Field>
          <Field label="Telefone"><Input data-testid="employee-phone" value={f.phone || ""} onChange={set("phone")} inputMode="tel" className={inputCls} /></Field>
          <Field label="Data de nascimento"><input data-testid="employee-birth-date" type="date" value={f.birth_date || ""} onChange={set("birth_date")} className={selectCls} /></Field>
          <Field label="Admissão"><input data-testid="employee-admission" type="date" value={f.admission_date || ""} onChange={set("admission_date")} className={selectCls} /></Field>
          <Field label="Salário (R$)"><Input data-testid="employee-salary" type="number" step="0.01" value={f.salary} onChange={set("salary")} className={inputCls} /></Field>
          {f.role === "mecanico" && (
            <Field label="Comissão por hora trabalhada (R$/h)"><Input data-testid="employee-hour-rate" type="number" step="0.01" value={f.comm_hour_rate} onChange={set("comm_hour_rate")} className={inputCls} placeholder="opcional, usa o cronômetro" /></Field>
          )}
          {(f.role === "mecanico" || f.role === "consultor") && (
            <Field label="Meta mensal (R$)"><Input data-testid="employee-goal" type="number" step="0.01" value={f.monthly_goal} onChange={set("monthly_goal")} className={inputCls} placeholder="ex.: 20000" /></Field>
          )}
          <label className="flex items-center gap-2 text-sm text-zinc-300 self-end pb-2"><input data-testid="employee-active" type="checkbox" checked={f.active} onChange={set("active")} /> Ativo</label>
          {f.role === "consultor" && (<>
            <Field label="Comissão sobre peças (%)"><Input data-testid="employee-comm-parts" type="number" step="0.1" value={f.comm_parts_pct} onChange={set("comm_parts_pct")} className={inputCls} /></Field>
            <Field label="Comissão sobre serviços (%)"><Input data-testid="employee-comm-labor" type="number" step="0.1" value={f.comm_labor_pct} onChange={set("comm_labor_pct")} className={inputCls} /></Field>
          </>)}
          <label className="col-span-2 flex items-center gap-2 text-sm text-zinc-300"><input data-testid="employee-payable" type="checkbox" checked={f.salary_payable} onChange={set("salary_payable")} /> Gerar conta a pagar do salário todo dia 1 (vence dia 5)</label>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="employee-save" type="submit" disabled={busy || !f.name.trim()} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

import { HrPanel } from "@/components/HrPanel";

export default function OwnerEmployees() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState({ open: false, emp: null });
  const [pinDlg, setPinDlg] = useState({ open: false, emp: null });
  const load = useCallback(() => api.get("/employees").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5 animate-fade-up" data-testid="employees-page">
      <PageHead icon={Contact} kicker="Cadastro" title="Funcionários (RH)">
        <Button data-testid="employee-new-btn" disabled={user?.shop_id === "all"} className={btnPrimary} onClick={() => setEdit({ open: true, emp: null })}><Plus className="h-4 w-4 mr-1" /> Novo</Button>
      </PageHead>
      <p className="text-xs text-zinc-500">Mecânicos e consultores ficam aqui. Mecânicos aparecem na OS e no pátio; consultores são obrigatórios ao abrir uma nova ordem.</p>
      {!items ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : !items.length ? <Empty>Nenhum funcionário.</Empty> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((e) => (
            <div key={e.id} data-testid={`employee-card-${e.id}`} className={`rounded-2xl border border-card2-border bg-card2 p-4 ${e.active ? "" : "opacity-50"}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0"><div className="font-bold text-zinc-100 truncate">{e.name}</div>
                  <div className="text-[11px] text-zinc-400">{ROLE[e.role]}{e.job_title ? ` · ${e.job_title}` : ""}{e.active ? "" : " · inativo"}</div></div>
                <button data-testid={`employee-edit-${e.id}`} onClick={() => setEdit({ open: true, emp: e })} className="p-1.5 text-zinc-400 hover:text-green-neon"><Pencil className="h-4 w-4" /></button>
              </div>
              <div className="text-xs text-zinc-500 mt-2 space-y-0.5">
                {e.phone && <div>{e.phone}</div>}
                {e.admission_date && <div>Admissão {fmtDate(e.admission_date)}</div>}
                {e.salary != null && <div>Salário {brl(e.salary)}{e.salary_payable ? " · gera conta todo mês" : ""}</div>}
              </div>
              {e.role === "mecanico" && (
                <PinControls emp={e} onChanged={load} onOpenDialog={() => setPinDlg({ open: true, emp: e })} />
              )}
            </div>
          ))}
        </div>
      )}
      {items && <HrPanel employees={items} disabled={user?.shop_id === "all"} />}
      <EmployeeDialog open={edit.open} emp={edit.emp} onOpenChange={(o) => setEdit((p) => ({ ...p, open: o }))} onSaved={load} />
      <PinDialog open={pinDlg.open} emp={pinDlg.emp} onOpenChange={(o) => setPinDlg((p) => ({ ...p, open: o }))} onSaved={load} />
    </div>
  );
}

function PinControls({ emp, onChanged, onOpenDialog }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => api.get(`/employees/${emp.id}/pin-status`).then(({ data }) => setStatus(data)).catch(() => setStatus(null)), [emp.id]);
  useEffect(() => { refresh(); }, [refresh]);
  const toggle = async () => {
    setBusy(true);
    try { await api.post(`/employees/${emp.id}/pin/block`); await refresh(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await api.delete(`/employees/${emp.id}/pin`); toast.success("PIN removido"); await refresh(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`emp-pin-${emp.id}`} className="mt-3 pt-3 border-t border-zinc-800/80 flex flex-wrap items-center gap-2">
      <div className="text-[11px] font-mono-caps text-zinc-500 inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> PIN do checklist</div>
      <span data-testid={`emp-pin-status-${emp.id}`} className={`text-[11px] font-bold uppercase ${status?.has_pin ? (status.blocked ? "text-amber-300" : "text-green-300") : "text-zinc-500"}`}>
        {status?.has_pin ? (status.blocked ? "bloqueado" : "cadastrado") : "sem pin"}
      </span>
      <div className="ml-auto flex gap-1">
        <button type="button" data-testid={`emp-pin-set-${emp.id}`} onClick={onOpenDialog} disabled={busy} className="h-8 px-2 rounded-md border border-zinc-800 text-xs text-zinc-200 hover:bg-zinc-800">{status?.has_pin ? "Trocar" : "Definir"}</button>
        {status?.has_pin && (
          <>
            <button type="button" data-testid={`emp-pin-toggle-${emp.id}`} onClick={toggle} disabled={busy} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-800" title={status.blocked ? "Desbloquear" : "Bloquear"}>
              {status.blocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            </button>
            <button type="button" data-testid={`emp-pin-clear-${emp.id}`} onClick={clear} disabled={busy} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-zinc-800 text-red-400 hover:bg-red-950/50" title="Remover PIN"><Trash2 className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function PinDialog({ open, emp, onOpenChange, onSaved }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPin(""); }, [open]);
  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN deve ter 4 dígitos"); return; }
    setBusy(true);
    try { await api.put(`/employees/${emp.id}/pin`, { pin }); toast.success("PIN definido"); onSaved?.(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pin-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle className="font-display uppercase">PIN de 4 dígitos — {emp?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Field label="Novo PIN (apenas números)">
            <Input data-testid="pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} autoFocus placeholder="----" className={inputCls + " tracking-widest text-center text-2xl"} />
          </Field>
          <p className="text-[11px] text-zinc-500">O mecânico vai usar esse PIN no celular, na página /m/{(emp?.shop_slug) || "sua-oficina"}, para abrir o checklist das OS atribuídas a ele.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="pin-save" type="submit" disabled={busy || !/^\d{4}$/.test(pin)} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
